import { logYoutubeUploadError, logYoutubeUploadPhase } from './youtubeUploadDebug';

const PICK_METHOD = 'web-mobile-video-pick';

export function revokeMobileWebVideoBlobUri(uri) {
  if (uri && String(uri).startsWith('blob:') && typeof URL !== 'undefined') {
    try {
      URL.revokeObjectURL(uri);
    } catch {
      /* ignore */
    }
  }
}

/** Synchronous — call in the same turn as input change when possible. */
export function createMobileWebVideoBlobUri(file) {
  if (!file || typeof URL === 'undefined') return null;
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

export async function probeMobileWebVideoBlobUri(blobUri) {
  if (!blobUri || !String(blobUri).startsWith('blob:')) return 0;
  try {
    const resp = await fetch(blobUri);
    const blob = await resp.blob();
    return blob?.size || 0;
  } catch (err) {
    // Not a network failure — Android gallery picks often return blob URLs that cannot be read.
    logYoutubeUploadError(PICK_METHOD, 'pick-blob-probe', err, { note: 'blob-uri-unreadable' });
    throw err;
  }
}

/**
 * File System Access API — on Android Chrome this often opens Documents/Files
 * instead of the Photo Picker and returns a readable File with real size.
 */
export async function pickVideoViaShowOpenFilePicker() {
  if (typeof window === 'undefined' || typeof window.showOpenFilePicker !== 'function') {
    return null;
  }
  logYoutubeUploadPhase(PICK_METHOD, 'showOpenFilePicker-start', {});
  const [handle] = await window.showOpenFilePicker({
    types: [
      {
        description: 'Videos',
        accept: { 'video/*': ['.mp4', '.mov', '.webm', '.m4v', '.3gp'] },
      },
    ],
    multiple: false,
  });
  const file = await handle.getFile();
  logYoutubeUploadPhase(PICK_METHOD, 'showOpenFilePicker-ok', {
    fileName: file?.name,
    fileSize: file?.size,
  });
  return file;
}

export async function validateMobileWebVideoSelection({ file, blobUri, pickSource }) {
  if (file?.size > 0) {
    logYoutubeUploadPhase(PICK_METHOD, 'pick-valid', { pickSource, fileSize: file.size });
    return { ok: true, fileSize: file.size };
  }
  // Gallery picks on Android often report size 0 — blob probe usually fails too.
  if (pickSource === 'file-input') {
    logYoutubeUploadPhase(PICK_METHOD, 'pick-gallery-needs-materialize', {
      pickSource,
      reportedSize: file?.size,
    });
    return {
      ok: false,
      needsFilesFallback: true,
      message:
        'Couldn\u2019t read this video from Gallery. Tap Browse Files and pick the same video from Downloads.',
    };
  }
  if (!blobUri) {
    logYoutubeUploadError(PICK_METHOD, 'pick-no-blob-uri', new Error('No blob URI'), { pickSource });
    return { ok: false, message: 'Could not read this video. Please try again.' };
  }
  try {
    const probedSize = await probeMobileWebVideoBlobUri(blobUri);
    if (probedSize > 0) {
      logYoutubeUploadPhase(PICK_METHOD, 'pick-blob-probe-ok', { pickSource, fileSize: probedSize });
      return { ok: true, fileSize: probedSize };
    }
  } catch {
    /* logged in probeMobileWebVideoBlobUri */
  }
  logYoutubeUploadError(PICK_METHOD, 'pick-unreadable', new Error('Video unreadable'), {
    pickSource,
    reportedSize: file?.size,
  });
  return {
    ok: false,
    needsFilesFallback: true,
    message:
      'Couldn\u2019t read this video. Tap Browse Files and pick the .mp4 from Downloads, or paste a YouTube URL instead.',
  };
}
